import React from "react";
import { useTranslation } from "react-i18next";
import Container from "react-bootstrap/Container";
import styled from "styled-components";
import { Col, Form, InputGroup, Row } from "react-bootstrap";
import { SchoolTeacher } from "../../common/api";
import { ApiContext } from "../../common/context/ApiContext";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import { Account } from "../../common/context/AccountContext";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleMinus } from "@fortawesome/pro-regular-svg-icons";
import {
  COLOR_GRAY_50,
  COLOR_GRAY_MUTED,
  COLOR_RED,
} from "../../common/colors";

const Subhead = styled.span`
  color: ${COLOR_GRAY_MUTED};
`;

const TeacherCount = styled.span`
  font-weight: 600;
`;

const UnselectTeacherButton = styled(FontAwesomeIcon)`
  cursor: pointer;
  padding-right: 0.5em;
  width: 1em;
  color: ${COLOR_RED};
`;

const TeacherNameSearchInput = styled(Form.Control)`
  background-color: ${COLOR_GRAY_50};
  border: none;
  border-radius: 2rem;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;

  ::placeholder {
    color: ${COLOR_GRAY_MUTED};
    padding-left: 100px;
  }
`;

export const labelForTeacher = (s: Readonly<SchoolTeacher>) =>
  `${s.first_name} ${s.last_name}`;

// TODO: use React.memo to optimize rerenders?
//
// NOTE: This is a nearly a direct copy of StudentSelector. However, since
// Teachers function on user ids and Students function on profile ids, I made
// them distinct to avoid confusion about what information we are dealing with
// at any given time
export const TeacherSelector: React.FC<{
  account: Account;
  selectedTeachers: ReadonlyMap<number, Readonly<SchoolTeacher>>;
  setSelectedTeachers: (
    selectedUsers: ReadonlyMap<number, Readonly<SchoolTeacher>>
  ) => void;
}> = ({ account, selectedTeachers, setSelectedTeachers }) => {
  const [teacherNameFilter, setTeacherNameFilter] = React.useState("");
  const [distinctTeachers, setDistinctTeachers] = React.useState<
    Readonly<SchoolTeacher>[]
  >([]);
  const [visibleTeachers, setVisibleTeachers] = React.useState<
    Readonly<SchoolTeacher>[]
  >([]);
  const { t } = useTranslation();
  const { getTeachers } = React.useContext(ApiContext);
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const dataFetchedForUserVersion = React.useRef<number | null>(null);
  const fetchJobForUserVersion = React.useRef<number | null>(null);

  React.useEffect(() => {
    (async () => {
      if (
        account.userCurrentVersion !== dataFetchedForUserVersion.current &&
        account.userCurrentVersion !== fetchJobForUserVersion.current
      ) {
        startLoading();
        const expectedVersion = (fetchJobForUserVersion.current =
          account.userCurrentVersion);
        fetchJobForUserVersion.current = account.userCurrentVersion;
        const { teachers } = await getTeachers();
        if (expectedVersion !== fetchJobForUserVersion.current) {
          return;
        }
        setDistinctTeachers(teachers);
        dataFetchedForUserVersion.current = fetchJobForUserVersion.current;
        stopLoading();
      }
    })();
  }, [account, getTeachers, setDistinctTeachers, startLoading, stopLoading]);

  // Calculate visible list of teachers based on filter.
  // Must be done every time we reload teachers from API or name filter changes.
  React.useEffect(() => {
    if (teacherNameFilter) {
      const lowerCaseFilter = teacherNameFilter.toLocaleLowerCase();
      const filtered = distinctTeachers.filter((teacher) =>
        teacher.first_name.toLocaleLowerCase().includes(lowerCaseFilter)
      );
      setVisibleTeachers(filtered);
    } else {
      setVisibleTeachers(distinctTeachers);
    }
  }, [teacherNameFilter, distinctTeachers, setVisibleTeachers]);

  const onChangeTeacherCheckbox = React.useCallback(
    (changedTeacher: Readonly<SchoolTeacher>, isChecked: boolean) => {
      const newSelectedTeachers = new Map(selectedTeachers);
      if (isChecked) {
        newSelectedTeachers.set(changedTeacher.id, changedTeacher);
      } else {
        newSelectedTeachers.delete(changedTeacher.id);
      }
      setSelectedTeachers(newSelectedTeachers);
    },
    [selectedTeachers, setSelectedTeachers]
  );

  if (!distinctTeachers) {
    return <></>;
  }

  return (
    <Container className="my-4" style={{ maxWidth: "800px" }}>
      {/* Filters and teacher list are laid out differently for diff widths:
          - xs, sm and md: 1 column, filters above list
          - lg+: 2 columns, filters then list
          Subhead appears above filters always. */}
      <Row>
        {/* Filters */}
        <Col
          xs={{ span: 8, offset: 2 }}
          lg={{ span: 6, offset: 0 }}
          className="h-100"
        >
          <Row className="justify-content-center mb-3">
            <Col xs="auto">
              <Subhead>
                {t("create_instructional_group_teacher_selector")}
              </Subhead>
            </Col>
          </Row>
          <Row>
            {/* "Search teacher by name" input field */}
            <InputGroup className="mb-3">
              <TeacherNameSearchInput
                className="ps-4"
                placeholder={t(
                  "create_instructional_group_search_by_teacher_name"
                )}
                aria-label={t(
                  "create_instructional_group_search_by_teacher_name"
                )}
                value={teacherNameFilter}
                onChange={(e) => setTeacherNameFilter(e.target.value)}
              />
            </InputGroup>
          </Row>
          <FormGroup>
            {visibleTeachers.map((teacher) => {
              return (
                <Row key={teacher.id} className="ps-2">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedTeachers.has(teacher.id)}
                        onChange={(event) =>
                          onChangeTeacherCheckbox(teacher, event.target.checked)
                        }
                      />
                    }
                    label={labelForTeacher(teacher)}
                  ></FormControlLabel>
                </Row>
              );
            })}
          </FormGroup>
        </Col>
        {/* Vertical bar for lg+ as tall as the first column */}
        <Col className="d-none d-lg-flex justify-content-center" xs={0} lg={1}>
          <div className="vr h-100"></div>
        </Col>
        {/* Second column visible only in lg+ */}
        <Col className="d-none d-lg-inline" xs={0} lg={5}>
          <Row className="justify-content-center mb-3">
            <Col xs="auto">
              <TeacherCount>{`${selectedTeachers.size} `}</TeacherCount>
              <span>
                {t("create_instructional_group_teachers_selected", {
                  count: selectedTeachers.size,
                })}
              </span>
            </Col>
          </Row>
          {distinctTeachers
            .filter((s) => selectedTeachers.has(s.id))
            .map((teacher) => (
              <Row key={teacher.id} className="my-2">
                <Col>
                  <UnselectTeacherButton
                    icon={faCircleMinus}
                    onClick={() => onChangeTeacherCheckbox(teacher, false)}
                  />
                  <span style={{ alignContent: "center" }}>
                    {teacher.first_name}
                  </span>
                </Col>
              </Row>
            ))}
        </Col>
      </Row>
    </Container>
  );
};
